import { auth } from '../firebase/firebaseConfig';

/**
 * Service for server-side resume export (PDF/DOCX)
 * Communicates with resumeglobe-backend API
 */
export class ServerExportService {
  static BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';

  /**
   * Generate PDF from HTML using server-side Puppeteer
   */
  static async generatePDF(html, fileName, userId) {
    try {
      console.log(`📤 Sending PDF generation request to server...`);

      // Call backend API (no authentication needed - handled in frontend)
      const response = await fetch(`${this.BACKEND_URL}/api/export/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          html,
          fileName,
          userId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', errorText);
        console.error('❌ Response status:', response.status);
        console.error('❌ Response headers:', Object.fromEntries(response.headers.entries()));
        
        let errorMessage = 'PDF generation failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      // Get PDF blob
      const blob = await response.blob();
      
      console.log(`✅ PDF received from server`);
      console.log(`   Size: ${blob.size} bytes`);
      console.log(`   Type: ${blob.type}`);
      console.log(`   Response status: ${response.status}`);
      console.log(`   Content-Type header: ${response.headers.get('content-type')}`);

      // Verify it's a PDF
      if (blob.type !== 'application/pdf' && blob.size > 0) {
        console.warn('⚠️ Response is not a PDF! Type:', blob.type);
        // Try to read as text to see error
        const text = await blob.text();
        console.error('Response content (first 500 chars):', text.substring(0, 500));
        throw new Error('Invalid PDF response from server');
      }
      
      if (blob.size === 0) {
        throw new Error('Received empty PDF file from server');
      }

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { 
        success: true,
        size: blob.size,
        blob: blob  // Return blob for Firebase Storage upload
      };

    } catch (error) {
      console.error('❌ Server PDF export error:', error);
      throw error; // Re-throw so caller can handle
    }
  }

  /**
   * Generate DOCX from HTML + structured resume data using server-side docx package.
   * Passing resumeData lets the backend produce a properly structured Word document
   * (with headings, bullets, correct fonts) instead of a Pandoc plain-text conversion.
   */
  static async generateDOCX(html, fileName, userId, resumeData = null, fontFamily = 'Calibri', colorTheme = 'blue', templateId = null) {
    try {
      console.log(`📤 Sending DOCX generation request to server...`);

      // Call backend API (no authentication needed - handled in frontend)
      const response = await fetch(`${this.BACKEND_URL}/api/export/docx`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          html,
          fileName: fileName.replace('.pdf', '.docx'),
          userId,
          resumeData,   // structured data for high-quality docx generation
          fontFamily,   // user-selected font
          colorTheme,   // color palette name for accent colors
          templateId    // template ID for layout preset selection
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Backend error response:', errorText);
        
        let errorMessage = 'DOCX generation failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }

        throw new Error(errorMessage);
      }

      // Get DOCX blob
      const blob = await response.blob();
      
      console.log(`✅ DOCX received from server, size: ${blob.size} bytes, type: ${blob.type}`);

      // Verify it's a DOCX
      const expectedType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (!blob.type.includes('officedocument') && blob.size > 0) {
        console.warn('⚠️ Response is not a DOCX! Type:', blob.type);
        // Try to read as text to see error
        const text = await blob.text();
        console.error('Response content:', text);
        throw new Error('Invalid DOCX response from server');
      }

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName.replace('.pdf', '.docx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        size: blob.size,
        blob: blob  // Return blob for Firebase Storage upload
      };

    } catch (error) {
      console.error('❌ Server DOCX export error:', error);
      throw error; // Re-throw so caller can handle
    }
  }

  /**
   * Check export service health
   */
  static async checkHealth() {
    try {
      const response = await fetch(`${this.BACKEND_URL}/api/export/health`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ Export service health check failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Test backend connection
   */
  static async testConnection() {
    try {
      const response = await fetch(`${this.BACKEND_URL}/health`);
      const data = await response.json();
      return {
        success: true,
        message: data.message
      };
    } catch (error) {
      return {
        success: false,
        error: 'Cannot connect to backend server. Make sure it is running.'
      };
    }
  }
}
